import {
  Autocomplete,
  Box,
  Grid,
  IconButton,
  MenuItem,
  Popper,
  TextField,
  Typography,
} from '@mui/material';
import { createFilterOptions } from '@mui/material/Autocomplete';
import { styled } from '@mui/material/styles';
import clsx from 'clsx';
import React, { useCallback, useEffect, useState } from 'react';

import { useTranslation } from 'common/utils/getLocalString';

import ChevronIcon from '../../../../../packages/common/shared-ui/src/icons/ChevronIcon';
import DeleteIconBig from '../../../../../packages/common/shared-ui/src/icons/DeleteIconBig';
import PlusIcon from '../../../../../packages/common/shared-ui/src/icons/PlusIcon';
import SubmitIcon from '../../../../../packages/common/shared-ui/src/icons/SubmitIcon';

const StyledRowAutocomlpete = styled(Autocomplete, {
  shouldForwardProp: prop => !['changedStyle'].includes(prop),
})(({ theme, changedStyle }) => ({
  '&.MuiAutocomplete-root': {
    width: '100%',
  },
  '& .MuiInputBase-root.MuiOutlinedInput-root': {
    padding: '0',
    lineHeight: '20px',
    height: 'auto',
    '& .MuiAutocomplete-endAdornment': {
      right: 0,
      '& .MuiButtonBase-root.MuiIconButton-root.MuiAutocomplete-clearIndicator':
        {
          '& .MuiSvgIcon-root': {
            width: '20px',
            height: '20px',
          },
        },
      '& .MuiSvgIcon-root': {
        width: '20px',
        height: '20px',
        position: 'static',
      },
    },
    '& .MuiAutocomplete-input': {
      fontSize: '14px',
      lineHeight: '20px',
      color: '#7174AC',
      padding: '10px',
      height: 'auto',
      ...(changedStyle && {
        color: '#212346',
      }),
    },
    '& .MuiOutlinedInput-notchedOutline': {
      padding: 0,
      border: 'none',
    },
  },
  '&.Mui-expanded': {
    '& .MuiInputBase-root.MuiOutlinedInput-root': {
      borderRadius: '16px 16px 0 0',
      '& .MuiOutlinedInput-notchedOutline': {
        padding: 0,
      },
    },
    '& .MuiFormLabel-root.MuiInputLabel-root': {
      display: 'none',
    },
  },
  '&.Mui-focused': {
    '& .MuiFormLabel-root.MuiInputLabel-root': {
      display: 'none',
    },
  },
  '& + .MuiAutocomplete-popper': {
    minWidth: 'auto!important',
    '& .MuiPaper-root.MuiAutocomplete-paper': {
      borderRadius: '0 0 16px 16px',
      borderTop: 'none',
      boxShadow: '0px 5px 10px -8px #A0A1AA66',
      '& .MuiAutocomplete-noOptions': {
        padding: '8px 16px',
        color: '#212346',
        fontSize: '12px',
        lineHeight: '20px',
      },
      '& .MuiAutocomplete-listbox': {
        padding: '0',
        '& li': {
          whiteSpace: 'normal',
          fontSize: '12px',
          lineHeight: '20px',
          color: '#212346',
          '&.Mui-focused': {
            backgroundColor: 'transparent',
          },
          '&:hover': {
            backgroundColor: 'rgba(0, 0, 0, 0.04)',
          },
          '&.Mui-selected': {
            backgroundColor: '#DCDFF4',
          },
        },
        '&::-webkit-scrollbar': {
          width: '2px',
        },
        '&::-webkit-scrollbar-thumb': {
          backgroundColor: '#7174AC',
          borderRadius: '5px',
        },
      },
    },
  },
}));
const StyledGridPopper = styled(Popper)(theme => ({
  zIndex: 90,

  '& .MuiPaper-root.MuiAutocomplete-paper': {
    borderRadius: '0 0 16px 16px',
    borderTop: '1px solid #E4E4EF',
    boxShadow: '0px 5px 10px -8px #A0A1AA66',
    '& .MuiAutocomplete-noOptions': {
      padding: '8px 16px',
      color: '#212346',
      fontSize: '12px',
      lineHeight: '20px',
    },
    '& .MuiAutocomplete-listbox': {
      padding: '0',
      '& li': {
        padding: '8px 10px',
        whiteSpace: 'normal',
        fontSize: '12px',
        lineHeight: '20px',
        color: '#212346',
        '& .MuiButtonBase-root.MuiCheckbox-root': {
          padding: '2px',
          marginRight: '10px',
        },
        '&.Mui-focused': {
          backgroundColor: 'transparent',
        },
        '&:hover': {
          backgroundColor: 'rgba(0, 0, 0, 0.04)',
        },
        '&.Mui-selected': {
          backgroundColor: '#DCDFF4',
        },
      },
      '&::-webkit-scrollbar': {
        width: '2px',
      },
      '&::-webkit-scrollbar-thumb': {
        backgroundColor: '#7174AC',
        borderRadius: '5px',
      },
    },
  },
}));
const ObjectsHierarchyColumnItem = ({
  item,
  options,
  handleAction,
  relation,
  index,
}) => {
  const getLocalString = useTranslation();
  const ObjectActions = { SAVE: 'save', DELETE: 'delete' };

  const [currentItem, setCurrentItem] = useState(null);
  const [selectedObject, setSelectedObject] = useState(null);
  const [currentOptions, setCurrentOptions] = useState([]);

  useEffect(() => {
    if (item) {
      setCurrentItem(item);
    }
  }, [item]);

  useEffect(() => {
    if (options) {
      setCurrentOptions(options);
    }
  }, [options]);

  const handleSelectObject = useCallback(
    object => {
      setSelectedObject(object);
      setCurrentItem({ ...currentItem, ...object });
    },
    [currentItem],
  );

  const handleObjectAction = useCallback(
    type => {
      handleAction(currentItem, type, relation, index);
    },
    [currentItem, handleAction, index, relation],
  );
  const filterOptions = createFilterOptions({
    matchFrom: 'start',
    stringify: option => option.valueName,
  });
  return (
    <>
      {currentItem && (
        <Grid borderBottom={'1px solid #E4E4EF'}>
          <Grid container>
            <Grid flex={1}>
              {currentItem.new ? (
                <StyledRowAutocomlpete
                  filterOptions={filterOptions}
                  noOptionsText={getLocalString('labels', 'notFound')}
                  onChange={(event, newValue) => handleSelectObject(newValue)}
                  options={currentOptions}
                  disableClearable
                  renderOption={(props, option) => {
                    return (
                      <MenuItem
                        {...props}
                        key={option.valueId}
                        value={option.valueId}
                      >
                        {option.valueName}
                      </MenuItem>
                    );
                  }}
                  value={currentItem ? currentItem : ''}
                  isOptionEqualToValue={(option, value) =>
                    option.value === value.value
                  }
                  getOptionLabel={option => option.valueName || ''}
                  slots={{
                    popper: props => (
                      <StyledGridPopper
                        {...props}
                        placement='bottom-start'
                      ></StyledGridPopper>
                    ),
                  }}
                  renderInput={params => (
                    <TextField
                      {...params}
                      placeholder={getLocalString('labels', 'select')}
                    />
                  )}
                  popupIcon={<ChevronIcon direction={'down'} />}
                />
              ) : (
                <Box padding={'10px'}>
                  <Typography variant='h4' color={'#082253'}>
                    {currentItem.valueName}
                  </Typography>
                </Box>
              )}
            </Grid>
            {currentItem.new && (
              <IconButton
                disabled={!currentItem.valueId}
                onClick={() => handleObjectAction(ObjectActions.SAVE)}
                sx={{
                  padding: '0 8px',
                  borderLeft: '1px solid #E4E4EF',
                  borderRadius: 0,
                }}
              >
                <SubmitIcon />
              </IconButton>
            )}
            <IconButton
              onClick={() => handleObjectAction(ObjectActions.DELETE)}
              sx={{
                padding: '0 8px',
                borderLeft: '1px solid #E4E4EF',
                borderRadius: 0,
              }}
            >
              <DeleteIconBig />
            </IconButton>
          </Grid>
        </Grid>
      )}
    </>
  );
};

export default ObjectsHierarchyColumnItem;
